/**
 * Product information v1 — gallery, variants, ATC, carousels
 */
(function () {
  'use strict';

  function money(cents, format) {
    var fmt = format
      || (window.theme && theme.settings && theme.settings.moneyFormat)
      || '${{amount}}';
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, fmt);
    }
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, fmt);
    }
    var amount = (Number(cents) / 100).toFixed(2);
    return '$' + amount;
  }

  function parseVariants(root) {
    var el = root.querySelector('[data-piv1-variants-json]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent || '[]');
    } catch (e) {
      return [];
    }
  }

  function isDrawerCart() {
    return !!(window.theme && theme.settings && theme.settings.cartType === 'drawer');
  }

  function fireCartEvents(detail) {
    var ev = function (name) {
      document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
    };
    ev('ajaxProduct:added');
    ev('cart-drawer:open');
    if (window.CartDrawerRaf && typeof window.CartDrawerRaf.open === 'function') {
      window.CartDrawerRaf.open();
    }
  }

  function syncThumbSize(gallery, root) {
    if (!gallery || window.matchMedia('(max-width: 768px)').matches) {
      if (gallery) {
        gallery.style.removeProperty('--piv1-thumb-w');
        gallery.style.removeProperty('--piv1-thumb-h');
        gallery.style.removeProperty('--piv1-main-h');
        gallery.style.removeProperty('--piv1-thumb-size');
      }
      return;
    }
    var main = gallery.querySelector('[data-piv1-main]');
    if (!main) return;
    var visible = parseInt((root && root.getAttribute('data-piv1-thumbs-visible')) || '5', 10);
    if (!visible || visible < 1) visible = 5;
    var gap = 8;
    var h = Math.round(main.getBoundingClientRect().height || main.offsetHeight || 0);
    if (h < 80) return;

    // Portrait 3:4 — size from main height only so exactly `visible` thumbs fit (no 6th peek)
    var thumbH = Math.floor((h - (visible - 1) * gap) / visible);
    while (visible * thumbH + (visible - 1) * gap > h && thumbH > 40) {
      thumbH -= 1;
    }
    if (thumbH < 56) thumbH = 56;
    if (thumbH > 180) thumbH = 180;
    var thumbW = Math.floor((thumbH * 3) / 4);
    if (thumbW < 48) thumbW = 48;

    gallery.style.setProperty('--piv1-main-h', h + 'px');
    gallery.style.setProperty('--piv1-thumb-h', thumbH + 'px');
    gallery.style.setProperty('--piv1-thumb-w', thumbW + 'px');
    gallery.style.setProperty('--piv1-thumbs-visible', String(visible));
    gallery.style.setProperty('--piv1-thumb-gap', gap + 'px');
  }

  function initGallery(root) {
    var gallery = root.querySelector('[data-piv1-gallery]');
    if (!gallery) return;
    var frames = Array.prototype.slice.call(gallery.querySelectorAll('[data-piv1-frame]'));
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-piv1-thumb]'));
    var dotsDesktop = Array.prototype.slice.call(gallery.querySelectorAll('[data-piv1-dots-desktop] [data-piv1-dot]'));
    var prevBtn = gallery.querySelector('[data-piv1-prev]');
    var nextBtn = gallery.querySelector('[data-piv1-next]');
    var counterMobile = gallery.querySelector('[data-piv1-counter-mobile] [data-piv1-counter-current]');
    var main = gallery.querySelector('[data-piv1-main]');
    var count = frames.length;
    if (!count) return;
    var index = 0;
    var mq = window.matchMedia('(max-width: 768px)');

    function syncDots(list, activeIndex) {
      list.forEach(function (d, i) {
        var on = i === activeIndex;
        d.classList.toggle('is-active', on);
        d.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    function syncCounters(imageIndex) {
      if (counterMobile) counterMobile.textContent = String(imageIndex + 1);
    }

    function syncNav() {
      if (!prevBtn || !nextBtn) return;
      if (mq.matches) {
        prevBtn.disabled = index <= 0;
        nextBtn.disabled = index >= count - 1;
      } else {
        prevBtn.disabled = index <= 0;
        nextBtn.disabled = count <= 2 || index >= count - 2;
      }
    }

    function setIndex(next) {
      index = Math.max(0, Math.min(count - 1, next));
      if (mq.matches) {
        frames.forEach(function (f) {
          f.hidden = false;
        });
        if (main && frames[index]) {
          main.scrollTo({ left: frames[index].offsetLeft, behavior: 'smooth' });
        }
        syncDots(dotsDesktop, Math.floor(index / 2));
        syncCounters(index);
      } else {
        var pairStart = index;
        if (pairStart >= count - 1 && count > 1) {
          pairStart = count - 2;
        }
        if (pairStart % 2 === 1) {
          pairStart = pairStart - 1;
        }
        pairStart = Math.max(0, pairStart);
        frames.forEach(function (f, i) {
          var show = i === pairStart || i === pairStart + 1;
          f.hidden = !show;
          f.classList.toggle('is-primary', i === pairStart);
          f.classList.toggle('is-secondary', i === pairStart + 1);
        });
        index = pairStart;
        syncDots(dotsDesktop, Math.floor(pairStart / 2));
        syncCounters(pairStart);
      }
      thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === index);
        t.setAttribute('aria-pressed', i === index ? 'true' : 'false');
        if (i === index && typeof t.scrollIntoView === 'function') {
          t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      });
      syncNav();
      syncThumbSize(gallery, root);
    }

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () {
        setIndex(parseInt(t.getAttribute('data-index'), 10) || 0);
      });
    });
    dotsDesktop.forEach(function (d) {
      d.addEventListener('click', function () {
        var pair = parseInt(d.getAttribute('data-pair-index'), 10);
        if (isNaN(pair)) pair = parseInt(d.getAttribute('data-index'), 10) || 0;
        setIndex(pair * 2);
      });
    });
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        if (mq.matches) setIndex(index - 1);
        else setIndex(index - 2);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (mq.matches) setIndex(index + 1);
        else setIndex(index + 2);
      });
    }

    if (main) {
      main.addEventListener('scroll', function () {
        if (!mq.matches) return;
        var best = 0;
        var bestDist = Infinity;
        frames.forEach(function (f, i) {
          var dist = Math.abs(f.offsetLeft - main.scrollLeft);
          if (dist < bestDist) {
            bestDist = dist;
            best = i;
          }
        });
        if (best !== index) {
          index = best;
          thumbs.forEach(function (t, i) {
            t.classList.toggle('is-active', i === index);
            t.setAttribute('aria-pressed', i === index ? 'true' : 'false');
          });
          syncDots(dotsDesktop, Math.floor(index / 2));
          syncCounters(index);
          syncNav();
        }
      }, { passive: true });
    }

    if (mq.addEventListener) mq.addEventListener('change', function () { setIndex(index); });
    else if (mq.addListener) mq.addListener(function () { setIndex(index); });

    setIndex(0);
    syncThumbSize(gallery, root);
    window.addEventListener('resize', function () { syncThumbSize(gallery, root); });
    if (typeof ResizeObserver !== 'undefined' && main) {
      new ResizeObserver(function () { syncThumbSize(gallery, root); }).observe(main);
    }

    root.addEventListener('piv1:variant', function (ev) {
      var mediaId = ev.detail && ev.detail.featured_media && ev.detail.featured_media.id;
      if (!mediaId) return;
      var thumb = gallery.querySelector('[data-piv1-thumb][data-media-id="' + mediaId + '"]');
      if (!thumb) return;
      setIndex(parseInt(thumb.getAttribute('data-index'), 10) || 0);
    });
  }

  function updateOosWidgets(root, available) {
    var form = root.querySelector('[data-piv1-form]');
    if (!form) return;
    Array.prototype.slice.call(form.children).forEach(function (el) {
      if (el.matches('button[type="submit"], select, input, [data-piv1-option], script, .piv1-opt, .piv1-atc, .piv1__hidden-select, .piv1__ship')) return;
      var hay = ((el.className || '') + ' ' + (el.id || '') + ' ' + (el.textContent || '')).toLowerCase();
      var looksOos =
        /bis|notify|back-in-stock|back_in_stock|outofstock|out-of-stock|out of stock|sold out\?|click here/.test(hay);
      if (!looksOos) return;
      el.hidden = !!available;
      el.setAttribute('aria-hidden', available ? 'true' : 'false');
      el.style.setProperty('display', available ? 'none' : 'block', 'important');
    });
  }

  function watchOosWidgets(root) {
    var form = root.querySelector('[data-piv1-form]');
    if (!form || typeof MutationObserver === 'undefined') return;
    var timer = null;
    var obs = new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        var available = root.getAttribute('data-piv1-variant-available') !== 'false';
        updateOosWidgets(root, available);
      }, 50);
    });
    obs.observe(form, { childList: true, subtree: true });
    updateOosWidgets(root, root.getAttribute('data-piv1-variant-available') !== 'false');
  }

  function initStickyGallery(root) {
    if (root.getAttribute('data-piv1-sticky-gallery') === 'false') return;
    var media = root.querySelector('.piv1__media');
    if (!media) return;

    function measure() {
      if (window.matchMedia('(max-width: 768px)').matches) {
        root.style.removeProperty('--piv1-sticky-top');
        return;
      }
      if (root.getAttribute('data-piv1-sticky-match-header') === 'false') {
        var fallback = parseInt(root.getAttribute('data-piv1-sticky-offset') || '100', 10);
        if (!isNaN(fallback)) root.style.setProperty('--piv1-sticky-top', fallback + 'px');
        return;
      }
      var gap = parseInt(root.getAttribute('data-piv1-sticky-gap') || '16', 10);
      if (isNaN(gap)) gap = 16;
      var header =
        document.querySelector('#SiteHeader') ||
        document.querySelector('.site-header') ||
        document.querySelector('[data-header]') ||
        document.querySelector('sticky-header') ||
        document.querySelector('header');
      var h = 0;
      if (header) {
        h = Math.ceil(header.getBoundingClientRect().height || header.offsetHeight || 0);
      }
      root.style.setProperty('--piv1-sticky-top', Math.max(24, h + gap) + 'px');
    }

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      var headerEl = document.querySelector('#SiteHeader, .site-header, sticky-header, header');
      if (headerEl) new ResizeObserver(measure).observe(headerEl);
    }
  }

  function initVariants(root) {
    var form = root.querySelector('[data-piv1-form]');
    var select = root.querySelector('[data-piv1-variant-select]');
    var priceEl = root.querySelector('[data-piv1-price]');
    var atc = root.querySelector('[data-piv1-atc]');
    var atcLabel = root.querySelector('[data-piv1-atc-label]');
    var variants = parseVariants(root);
    if (!form || !select || !variants.length) return;

    function currentOptions() {
      var opts = [];
      root.querySelectorAll('[data-piv1-option]').forEach(function (group) {
        var checked = group.querySelector('input[type="radio"]:checked');
        opts.push(checked ? checked.value : '');
      });
      return opts;
    }

    function findVariant(opts) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var ok = true;
        for (var j = 0; j < opts.length; j++) {
          if ((v.options && v.options[j]) !== opts[j] && v['option' + (j + 1)] !== opts[j]) {
            ok = false;
            break;
          }
        }
        if (ok) return v;
      }
      return null;
    }

    function optionAvailable(optionIndex, value, opts) {
      var probe = opts.slice();
      probe[optionIndex] = value;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var match = true;
        for (var j = 0; j < probe.length; j++) {
          if (!probe[j]) continue;
          if ((v.options && v.options[j]) !== probe[j] && v['option' + (j + 1)] !== probe[j]) {
            match = false;
            break;
          }
        }
        if (match && v.available) return true;
      }
      return false;
    }

    function applyVariant(v) {
      if (!v) return;
      select.value = String(v.id);
      Array.prototype.forEach.call(select.options, function (opt) {
        opt.selected = opt.value === String(v.id);
      });
      if (priceEl) priceEl.textContent = money(v.price, root.getAttribute('data-money-format'));
      var available = !!v.available;
      root.setAttribute('data-piv1-variant-available', available ? 'true' : 'false');
      updateOosWidgets(root, available);
      if (atc) {
        atc.disabled = !available;
        atc.setAttribute('data-pir-variant-unavailable', available ? 'false' : 'true');
      }
      if (atcLabel) {
        atcLabel.textContent = available
          ? (atcLabel.getAttribute('data-default-text') || 'ADD TO CART')
          : (atcLabel.getAttribute('data-sold-out') || 'SOLD OUT');
      }
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* ignore */ }
      root.dispatchEvent(new CustomEvent('piv1:variant', { detail: v, bubbles: false }));
    }

    function sync() {
      var opts = currentOptions();
      root.querySelectorAll('[data-piv1-option]').forEach(function (group) {
        var idx = parseInt(group.getAttribute('data-option-index'), 10) || 0;
        var valueEl = group.querySelector('[data-piv1-option-value]');
        var checked = group.querySelector('input[type="radio"]:checked');
        if (valueEl && checked) valueEl.textContent = checked.value;
        group.querySelectorAll('label').forEach(function (label) {
          var input = label.querySelector('input');
          if (!input) return;
          var avail = optionAvailable(idx, input.value, opts);
          label.classList.toggle('is-unavailable', !avail);
          label.classList.toggle('is-selected', !!input.checked);
        });
      });
      applyVariant(findVariant(opts));
    }

    form.addEventListener('change', sync);
    sync();
  }

  function initAtc(root) {
    var form = root.querySelector('[data-piv1-form]');
    var atc = root.querySelector('[data-piv1-atc]');
    if (!form || !atc) return;

    form.addEventListener('submit', function (e) {
      if (!isDrawerCart()) return;
      e.preventDefault();
      if (atc.disabled) return;

      var fd = new FormData(form);
      atc.classList.add('is-loading');
      atc.disabled = true;
      var loading = root.querySelector('[data-piv1-atc-loading]');
      var label = root.querySelector('[data-piv1-atc-label]');
      if (loading) loading.hidden = false;
      if (label) label.hidden = true;

      fetch((window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: fd
      })
        .then(function (r) { return r.json(); })
        .then(function (item) {
          fireCartEvents(item);
          if (window.theme && theme.cart && typeof theme.cart.getCart === 'function') {
            theme.cart.getCart();
          }
        })
        .catch(function () {
          form.submit();
        })
        .finally(function () {
          atc.classList.remove('is-loading');
          atc.disabled = root.getAttribute('data-piv1-variant-available') === 'false';
          if (loading) loading.hidden = true;
          if (label) label.hidden = false;
        });
    });
  }

  function mulberry32(seed) {
    var t = seed >>> 0;
    return function () {
      t += 0x6D2B79F5;
      var r = Math.imul(t ^ (t >>> 15), 1 | t);
      r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
  }

  function shuffleCarousel(carousel, track) {
    if (!carousel.hasAttribute('data-piv1-shuffle') || carousel.classList.contains('is-shuffled')) return;
    var items = Array.prototype.filter.call(track.children, function (el) {
      return el.classList && el.classList.contains('piv1-carousel__item');
    });
    if (items.length < 2) {
      carousel.classList.add('is-shuffled');
      return;
    }
    var seed = parseInt(carousel.getAttribute('data-piv1-shuffle-seed'), 10) || 0;
    if (!seed) seed = new Date().getDate() + 1;
    var rand = mulberry32(seed);
    for (var i = items.length - 1; i > 0; i--) {
      var j = Math.floor(rand() * (i + 1));
      var tmp = items[i];
      items[i] = items[j];
      items[j] = tmp;
    }
    var limit = parseInt(carousel.getAttribute('data-piv1-shuffle-limit'), 10) || items.length;
    items.forEach(function (el, idx) {
      if (idx < limit) track.appendChild(el);
      else if (el.parentNode) el.parentNode.removeChild(el);
    });
    carousel.classList.add('is-shuffled');
  }

  function initCarousels(root) {
    root.querySelectorAll('[data-piv1-carousel]').forEach(function (carousel) {
      var track = carousel.querySelector('[data-piv1-carousel-track]');
      var prev = carousel.querySelector('[data-piv1-carousel-prev]');
      var next = carousel.querySelector('[data-piv1-carousel-next]');
      if (!track) return;
      shuffleCarousel(carousel, track);

      function step() {
        var item = track.querySelector('.piv1-carousel__item');
        if (!item) return Math.round(track.clientWidth * 0.8);
        var styles = window.getComputedStyle(track);
        var gap = parseFloat(styles.columnGap || styles.gap) || 0;
        return Math.round(item.getBoundingClientRect().width + gap);
      }

      function sync() {
        var max = Math.max(0, track.scrollWidth - track.clientWidth);
        if (prev) prev.disabled = track.scrollLeft <= 2 || max <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2 || max <= 2;
      }

      if (prev) prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  function inchToCmValue(str) {
    return String(str).replace(/(\d+(?:\.\d+)?)/g, function (num) {
      var cm = Math.round(parseFloat(num) * 2.54 * 2) / 2;
      return cm % 1 === 0 ? String(cm) : cm.toFixed(1);
    });
  }

  function initSizeGuide(root) {
    var modal = root.querySelector('[data-pir-size-guide]');
    if (!modal) return;
    var dialog = modal.querySelector('[data-pir-sg-dialog]');
    var openers = root.querySelectorAll('[data-pir-sg-open]');
    var closers = modal.querySelectorAll('[data-pir-sg-close]');
    var unitBtns = modal.querySelectorAll('[data-pir-sg-unit]');
    var unitWrap = modal.querySelector('[data-pir-sg-units]');
    var labels = modal.querySelectorAll('[data-pir-sg-unit-label]');
    var measures = modal.querySelectorAll('[data-pir-sg-measure]');
    var lastFocus = null;

    function setUnit(unit) {
      if (!unitWrap) return;
      unitWrap.setAttribute('data-unit', unit);
      unitBtns.forEach(function (btn) {
        var active = btn.getAttribute('data-pir-sg-unit') === unit;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      labels.forEach(function (el) {
        el.textContent = '(' + unit + ')';
      });
      measures.forEach(function (cell) {
        var inches = cell.getAttribute('data-in') || '';
        cell.textContent = unit === 'cm' ? inchToCmValue(inches) : inches;
      });
    }

    function open() {
      lastFocus = document.activeElement;
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('pir-sg-open');
      if (dialog) {
        dialog.scrollTop = 0;
        try { dialog.focus({ preventScroll: true }); } catch (e) {
          try { dialog.focus(); } catch (e2) {}
        }
      }
    }

    function close() {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('pir-sg-open');
      if (lastFocus && typeof lastFocus.focus === 'function') {
        try { lastFocus.focus(); } catch (e) {}
      }
    }

    openers.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        open();
      });
    });
    closers.forEach(function (el) {
      el.addEventListener('click', close);
    });
    unitBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        setUnit(btn.getAttribute('data-pir-sg-unit') || 'in');
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });

    if (unitWrap) setUnit(unitWrap.getAttribute('data-unit') || 'in');
  }

  function findReviewsTarget(id) {
    if (!id) return null;
    var target = document.getElementById(id);
    if (target) return target;
    return document.querySelector(
      '#looxReviews, #LooxReviews, [id*="looxReviews"], [id*="loox-reviews"], .loox-reviews-default'
    );
  }

  function initRatingScroll(root) {
    root.querySelectorAll('[data-piv1-scroll-to]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var href = link.getAttribute('data-piv1-scroll-to') || link.getAttribute('href') || '';
        var id = href.replace(/^#/, '');
        var target = findReviewsTarget(id);
        if (!target) return;
        e.preventDefault();
        var header = document.querySelector('#SiteHeader, .site-header, sticky-header, [data-header]');
        var offset = 24;
        if (header) {
          offset += header.getBoundingClientRect().height || 0;
        }
        var top = target.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        try {
          history.replaceState(null, '', '#' + (target.id || id));
        } catch (err) {}
      });
    });
  }

  function initAccordion(root) {
    root.querySelectorAll('[data-piv1-acc-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('[data-piv1-acc-item]');
        if (!item) return;
        var panel = item.querySelector('.piv1-acc__panel');
        var open = item.classList.contains('is-open');
        var group = item.closest('[data-piv1-acc]');
        if (group && group.getAttribute('data-piv1-acc-single') === 'true') {
          group.querySelectorAll('[data-piv1-acc-item]').forEach(function (el) {
            if (el === item) return;
            el.classList.remove('is-open');
            var b = el.querySelector('[data-piv1-acc-btn]');
            var p = el.querySelector('.piv1-acc__panel');
            if (b) b.setAttribute('aria-expanded', 'false');
            if (p) p.hidden = true;
          });
        }
        item.classList.toggle('is-open', !open);
        btn.setAttribute('aria-expanded', String(!open));
        if (panel) panel.hidden = open;
      });
    });
  }

  function init(root) {
    if (!root || root.dataset.piv1Ready === '1') return;
    root.dataset.piv1Ready = '1';
    initGallery(root);
    initVariants(root);
    initAtc(root);
    initCarousels(root);
    if (window.Piv1FindSimilar && typeof window.Piv1FindSimilar.init === 'function') {
      window.Piv1FindSimilar.init(root);
    }
    initRatingScroll(root);
    initAccordion(root);
    initSizeGuide(root);
    initStickyGallery(root);
    watchOosWidgets(root);
  }

  function boot() {
    document.querySelectorAll('[data-piv1]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-piv1]');
    if (root) {
      delete root.dataset.piv1Ready;
      init(root);
    }
  });
})();
